import type { CompiledTrack, Ramp } from '../track/types'
import { nearBody } from '../track/xindex'

/** Anything that stands on ramps: players and rolling items. */
export interface RampBody {
  readonly x: number
  y: number
  readonly w: number
  readonly vy: number
}

/** Surface y of a ramp at world x (clamped to the ramp's span by the caller). */
export function rampSurfaceAt(r: Ramp, x: number): number {
  const t = ((x - r.x) * r.h) / r.w
  return r.dir === 'up' ? r.y + r.h - t : r.y + t
}

/**
 * Highest surface point under the player's horizontal span, or null when the spans do not
 * overlap. The surface is linear, so the highest point is at the uphill end of the overlap; this
 * makes the feet meet flat ground flush at both ends of a ramp.
 */
export function rampSupportY(r: Ramp, x: number, w: number): number | null {
  if (x + w <= r.x || x >= r.x + r.w) return null
  const sample = r.dir === 'up' ? Math.min(x + w, r.x + r.w) : Math.max(x, r.x)
  return rampSurfaceAt(r, sample)
}

/**
 * Height-field ramps (no side walls). Feet inside a ramp's body are lifted to the surface; feet
 * up to `snapPx` above it are pulled down, unless something else already caught the player this
 * step. Never applies while rising. Returns true when the player ends on a ramp.
 */
export function snapToRamps(
  track: CompiledTrack,
  p: RampBody,
  snapPx: number,
  alreadyLanded: boolean,
): boolean {
  if (p.vy < 0) return false
  let landed = alreadyLanded
  let onRamp = false
  for (const k of nearBody(track.index.ramps, p)) {
    const r = track.ramps[k]!
    const s = rampSupportY(r, p.x, p.w)
    if (s === null || p.y > r.y + r.h) continue
    if (p.y >= s || (!landed && p.y >= s - snapPx)) {
      p.y = s
      landed = true
      onRamp = true
    }
  }
  return onRamp
}
